/**
 * Панель справа: переписка клиента и его сделка рядом — посмотреть чат, поправить сделку и
 * оставить комментарий, не уходя с экрана (замечание владельца 08.10.26 на «Доставках»).
 *
 * Слева — та же лента, что в «Чатах» (`Thread`, с ответом клиенту и переключателем его каналов),
 * справа — общий блок сделки `DealBrief` с полем комментария, открытым сразу, и датами проката
 * сделки (у брони и выдачи — крупно; продление проката видно здесь), и лента истории.
 * На узком экране колонки — вкладками. Чат и правка сделки — по праву «Отвечать клиентам в чатах»; без него
 * панель показывает только историю сделки.
 */
import { useGetIdentity } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { type ReactNode, useEffect } from "react";

import { DealBrief, DealHistory, RentDates } from "@/components/chat/DealPanel";
import { CLIENT_CHATS, has } from "@/lib/access";
import { loadDealCard } from "@/lib/dealCard";
import type { CurrentUser } from "@/lib/providers";
import { Thread } from "@/pages/ChatsPage";

export type DealSideTab = "chat" | "deal";

const TABS: { id: DealSideTab; label: string }[] = [
  { id: "chat", label: "Чат" },
  { id: "deal", label: "Сделка" },
];

function History({ dealId }: { dealId: number }) {
  const q = useQuery({ queryKey: ["deal-card", dealId], queryFn: () => loadDealCard(dealId) });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Собираю историю…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">История не загрузилась: {q.error.message}</div>;
  return <DealHistory title="История сделки" events={q.data.events} errors={q.data.errors} />;
}

export function DealSide({
  dealId,
  chatId,
  tab,
  title,
  children,
  onTab,
  onChat,
  onClose,
}: {
  dealId: number;
  /** Чат клиента в «Чатах»; `null` — переписки нет, панель показывает одну сделку. */
  chatId: number | null;
  /** Какая колонка открыта на узком экране; на широком видны обе. */
  tab: DealSideTab;
  title: ReactNode;
  /** Своё у экрана — над блоком сделки (у «Доставок» — что и когда везём). */
  children?: ReactNode;
  onTab: (tab: DealSideTab) => void;
  /** Сотрудник переключил канал клиента в ленте. */
  onChat: (id: number) => void;
  onClose: () => void;
}) {
  const { data: user } = useGetIdentity<CurrentUser>();
  const canChat = has(user, CLIENT_CHATS);
  const chat = canChat ? chatId : null;
  const side: DealSideTab = chat ? tab : "deal";
  useEffect(() => {
    // Esc в поле ответа или комментария — не «закрыть панель»: набранное пропало бы.
    const esc = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.key === "Escape" && !el?.closest("input, textarea, select")) onClose();
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden />
      <aside
        className={`fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-bg shadow-xl ${
          chat ? "max-w-[min(76rem,100vw)]" : "max-w-[min(30rem,100vw)]"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        role="dialog"
        aria-label={`Сделка ${dealId}`}
        data-annot={`панель сделки ${dealId}`}
      >
        <div className="flex flex-none items-center gap-3 border-b border-border px-4 py-2.5">
          <h2 className="min-w-0 truncate text-[14px] font-semibold">{title}</h2>
          {chat && (
            <div role="tablist" aria-label="Колонки панели" className="flex gap-1 lg:hidden">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={side === t.id}
                  onClick={() => onTab(t.id)}
                  className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
                    side === t.id ? "border-accent bg-accent-dim text-fg" : "border-border text-fg-muted hover:bg-bg-hover"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <button type="button" onClick={onClose} className="ml-auto flex-none rounded-[var(--radius-ui)] p-1 hover:bg-bg-hover" title="Закрыть (Esc)">
            <X size={16} aria-hidden />
            <span className="sr-only">Закрыть</span>
          </button>
        </div>
        <div className="flex min-h-0 flex-1">
          {chat && (
            <div className={`tgc min-h-0 min-w-0 flex-1 md:pt-2 lg:flex ${side === "chat" ? "flex" : "hidden"}`} data-annot="чат клиента">
              <Thread key={chat} chatId={chat} onChat={onChat} />
            </div>
          )}
          <div
            className={`min-h-0 w-full flex-col gap-3 overflow-y-auto p-3 lg:flex ${side === "deal" ? "flex" : "hidden"} ${
              chat ? "lg:w-[440px] lg:flex-none lg:border-l lg:border-border" : ""
            }`}
          >
            {children}
            {canChat ? (
              <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3">
                <DealBrief dealId={dealId} chatId={chat} quickComment>
                  {({ deal, salon }) => <RentDates deal={deal} salon={salon} chatId={chat ?? 0} />}
                </DealBrief>
              </div>
            ) : (
              <div className="text-[12px] text-fg-faint">Чат клиента, правка сделки и комментарии — по праву «Отвечать клиентам в чатах».</div>
            )}
            <History dealId={dealId} />
          </div>
        </div>
      </aside>
    </>
  );
}
